import type { DroppedItem } from '~~/shared/documentDrop'
import { fullPathOf } from '~~/shared/items/itemPath'
import type { ItemRow } from '~~/shared/items/itemRow'

/**
 * A document descriptor ready to be assigned to a workflow slot.
 *
 * Projected from `ItemRow` rather than re-declared: every field is a column of the row a drop
 * carries, and `toDoc` below normalises the optional ones. `has_identity_data` is `Required<…>`
 * because `toDoc` resolves it to `boolean | null` — null when the drop payload doesn't carry it
 * (a surface that predates the flag). See MAR-168.
 */
export type ResolvedDocument =
  Pick<ItemRow, 'id' | 'name' | 'file_name' | 'contract_type' | 'status'>
  & Required<Pick<ItemRow, 'has_identity_data'>>

/**
 * Resolves a heterogeneous drop payload (documents + folders, possibly nested)
 * into a flat, de-duplicated list of documents.
 *
 * Folders are expanded client-side by walking `/api/items?path=` one level at a
 * time (which has no row cap, unlike `/api/search/items`) and recursing into
 * sub-folders. The drop payload carries each item's `path`/`slug` so a folder's
 * full path can be rebuilt without anything being pre-loaded in the browser.
 */
export function useDroppedDocuments() {
  const { apiFetch } = useApi()

  function toDoc(i: { id: string, type?: string, name?: string, file_name?: string | null, contract_type?: string | null, status?: string | null, has_identity_data?: boolean | null }): ResolvedDocument {
    return {
      id: i.id,
      name: i.name || i.file_name || i.id,
      file_name: i.file_name ?? null,
      contract_type: i.contract_type ?? null,
      status: i.status ?? null,
      has_identity_data: i.has_identity_data ?? null,
    }
  }

  async function expandFolder(fullPath: string, seen: Set<string>): Promise<ResolvedDocument[]> {
    let children: ItemRow[]
    try {
      children = await apiFetch<ItemRow[]>('/api/items', { params: { path: fullPath } })
    } catch {
      return []
    }
    const docs: ResolvedDocument[] = []
    for (const child of children) {
      if (child.type === 'document') {
        if (!seen.has(child.id)) { seen.add(child.id); docs.push(toDoc(child)) }
      } else if (child.type === 'folder') {
        docs.push(...await expandFolder(fullPathOf(child), seen))
      }
    }
    return docs
  }

  /** Resolve a drop payload to documents, preserving drop order, no duplicates. */
  async function resolveDroppedDocuments(items: DroppedItem[]): Promise<ResolvedDocument[]> {
    const seen = new Set<string>()
    const out: ResolvedDocument[] = []
    for (const item of items) {
      if (item.type === 'document') {
        if (!seen.has(item.id)) { seen.add(item.id); out.push(toDoc(item)) }
      } else if (item.type === 'folder') {
        out.push(...await expandFolder(fullPathOf(item), seen))
      }
    }
    return out
  }

  return { resolveDroppedDocuments }
}
